import { dependencies } from '@pn/core/dependencies';
import type { DataItem } from '@pn/core/domain/data';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { isNil } from 'lodash-es';
import {
  companyMonthlyStatsSlice,
  companyQuarterlyCorporateStatsSlice,
  type StatsAggregationType,
} from './companyStatsSlice';

export const useQuarterlyStatsStorage = () => {
  const { name, getInitialState } = companyQuarterlyCorporateStatsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    companyStats: useSliceSelector((state) => state.companyStats),
    loadedCompanyIds: useSliceSelector((state) => state.loadedCompanyIds),
    aggregatedStats: useSliceSelector((state) => state.aggregatedStats),
    isFetchingAggregated: useSliceSelector(
      (state) => state.isFetchingAggregated
    ),
  };
};

export const quarterlyStatsActions = () => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = companyQuarterlyCorporateStatsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    requestAggregated: () => dispatch(actions.requestAggregated()),
    receive: (loadedCompanyId: string, stats: DataItem[]) =>
      dispatch(actions.receive({ loadedCompanyId, stats })),
    receiveAggregated: (
      aggregationType: StatsAggregationType,
      stats: DataItem[]
    ) => dispatch(actions.receiveAggregated({ aggregationType, stats })),
    error: () => dispatch(actions.error()),
    reset: () => dispatch(actions.reset()),
  };
};

export const useMonthlyStatsStorage = () => {
  const { name, getInitialState } = companyMonthlyStatsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    companyStats: useSliceSelector((state) => state.companyStats),
    loadedCompanyIds: useSliceSelector((state) => state.loadedCompanyIds),
  };
};

export const monthlyStatsActions = () => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = companyMonthlyStatsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    receive: (loadedCompanyId: string, stats: DataItem[]) =>
      dispatch(actions.receive({ loadedCompanyId, stats })),
    error: () => dispatch(actions.error()),
    reset: () => dispatch(actions.reset()),
  };
};
